import styled from "styled-components";
import { useTranslation } from "react-i18next";
import ContainerHeader from "../components/containerHeader";
import useCompanies from "../hooks/useCompanies";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import UserTable from "../components/userTable";
import CreateUserDialog from "../dialogs/createUserDialog";
import { useState } from "react";
import useUsers from "../hooks/useUsers";
import useDeleteCompanyUser from "../hooks/useDeleteCompanyUser";
import { useAuth } from "../contexts/authContext";

const CompanyUsersContainer = styled.div`
  position: relative;
`;

const UserTablesContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  padding-top: 0;
`;

const Description = styled.p`
  font-size: 0.9rem;
  max-width: 700px;
  padding: 1rem 2rem 0 2rem;
  text-align: left;
  color: var(--text-color-secondary);
  margin-bottom: 0;
`;

const CompanyUsers: React.FC = () => {
  const { t } = useTranslation();
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);

  const [companyId, organizationId, companyName, organizationName] = useUIStore(
    useShallow((state: UIState) => [
      state.companyId,
      state.organizationId,
      state.companyName,
      state.organizationName,
    ])
  );

  const { isAdminForSelectedCompanyOrOrganization } = useAuth();

  const {
    usersData,
    usersIsLoading,
    updateUser,
    isUpdatingUser,
    createNewUser,
    savingNewUser,
    sendUserInvite,
    sendingUserInvite,
  } = useUsers({
    enableUsers: !!companyId && isAdminForSelectedCompanyOrOrganization(),
    companyId: companyId as number,
    organizationId: organizationId as number,
  });

  const { deleteCompanyUser, deletingUser: isDeletingCompanyUser } =
    useDeleteCompanyUser({ companyId, organizationId });

  const { companies, isLoadingCompanies } = useCompanies();

  const handleAddUserClick = () => {
    setShowCreateUserDialog(true);
  };

  const filteredUsers = usersData?.filter((user) => {
    return user.email?.email?.endsWith("@rockon.io") === false;
  });

  return (
    <CompanyUsersContainer>
      <ContainerHeader
        title={t("header-all-users") + ` ${organizationName || companyName}`}
        description=""
      />
      <Description>{t("description-company-users")}</Description>
      <LoadingContainer
        status={
          isLoadingCompanies || usersIsLoading
            ? HttpRequestStatus.LOADING
            : HttpRequestStatus.SUCCESS
        }
      >
        <CreateUserDialog
          show={showCreateUserDialog}
          onHide={() => setShowCreateUserDialog(false)}
          createNewUser={createNewUser}
          companies={companies || []}
          savingNewUser={savingNewUser}
          sendUserInvite={sendUserInvite}
          sendingUserInvite={sendingUserInvite}
          preselectedCompany={
            companies?.find((c) => c.id === companyId) || null
          }
          preselectedOrganization={
            organizationId
              ? {
                  id: organizationId,
                  name: organizationName || "",
                }
              : undefined
          }
        />
        <UserTablesContainer>
          <UserTable
            users={filteredUsers || []}
            usersIsLoading={usersIsLoading}
            updateUser={updateUser}
            isUpdatingUser={isUpdatingUser}
            deleteUser={deleteCompanyUser}
            isDeletingUser={isDeletingCompanyUser}
            handleAddUserClick={handleAddUserClick}
            companies={companies || []}
            canManageRoles={isAdminForSelectedCompanyOrOrganization()}
          />
        </UserTablesContainer>
      </LoadingContainer>
    </CompanyUsersContainer>
  );
};

export default CompanyUsers;
